import { UserListItemResponseDtoStatus } from "@/spz-api/model";

export class UserStatus {
  private _value: "confirmed" | "awaiting" | "deactivated";
  private _labelKey: string;
  private _descriptionKey: string;
  private _confirmationTitleKey?: string;
  private _confirmationDescriptionKey?: string;
  private _canTransitionTo: UserStatus[];
  private constructor(
    value: "confirmed" | "awaiting" | "deactivated",
    labelKey: string,
    descriptionKey: string,
    canTransitionTo: UserStatus[] = [],
    confirmationTitleKey?: string,
    confirmationDescriptionKey?: string,
  ) {
    this._value = value;
    this._labelKey = labelKey;
    this._descriptionKey = descriptionKey;
    this._canTransitionTo = canTransitionTo;
    this._confirmationTitleKey = confirmationTitleKey;
    this._confirmationDescriptionKey = confirmationDescriptionKey;
  }

  private setCanTransitionTo(statuses: UserStatus[]): void {
    this._canTransitionTo = statuses;
  }

  static AWAITING: UserStatus;
  static CONFIRMED: UserStatus;
  static DEACTIVATED: UserStatus;

  static {
    // Initialize all statuses first
    UserStatus.AWAITING = new UserStatus(
      "awaiting",
      "user_status_awaiting",
      "",
    );
    UserStatus.CONFIRMED = new UserStatus(
      "confirmed",
      "user_status_confirmed",
      "user_status_confirmed_description",
      [],
      "activate_user_confirmation_title",
      "activate_user_confirmation_description",
    );
    UserStatus.DEACTIVATED = new UserStatus(
      "deactivated",
      "user_status_deactivated",
      "user_status_deactivated_description",
      [],
      "deactivate_user_confirmation_title",
      "deactivate_user_confirmation_description",
    );

    // Now set up the transitions
    UserStatus.CONFIRMED.setCanTransitionTo([UserStatus.DEACTIVATED]);
    UserStatus.DEACTIVATED.setCanTransitionTo([UserStatus.CONFIRMED]);
  }

  static fromValue(value: string): UserStatus {
    switch (value) {
      case "confirmed":
        return UserStatus.CONFIRMED;
      case "awaiting":
        return UserStatus.AWAITING;
      case "deactivated":
        return UserStatus.DEACTIVATED;
      default:
        throw new Error(`Invalid UserStatus value: ${value}`);
    }
  }

  static fromApi(value: UserListItemResponseDtoStatus): UserStatus {
    switch (value) {
      case UserListItemResponseDtoStatus.CONFIRMED:
        return UserStatus.CONFIRMED;
      case UserListItemResponseDtoStatus.AWAITING:
        return UserStatus.AWAITING;
      case UserListItemResponseDtoStatus.DEACTIVATED:
        return UserStatus.DEACTIVATED;
    }
  }

  static get values(): readonly UserStatus[] {
    return [UserStatus.CONFIRMED, UserStatus.AWAITING, UserStatus.DEACTIVATED];
  }

  static getOptions(currentStatus: UserStatus) {
    const availableStatuses = [currentStatus, ...currentStatus.canTransitionTo];

    return availableStatuses.map((status) => ({
      value: status.value,
      labelKey: status.labelKey,
      descriptionKey: status.descriptionKey || undefined,
      disabled: status === currentStatus,
    }));
  }

  get value() {
    return this._value;
  }

  get labelKey() {
    return this._labelKey;
  }

  get descriptionKey() {
    return this._descriptionKey;
  }

  get confirmationTitleKey() {
    return this._confirmationTitleKey;
  }

  get confirmationDescriptionKey() {
    return this._confirmationDescriptionKey;
  }

  get canTransitionTo() {
    return this._canTransitionTo;
  }
}
